import React from 'react'
import {useEvent} from 'Event/EventProvider'
import {ValidationError} from 'lib/ui/api-client'
import {api} from 'lib/url'
import {Area} from 'organization/Event/AreasProvider'
import {useOrganization} from 'organization/OrganizationProvider'
import {useState} from 'react'
import {useForm} from 'react-hook-form'
import Page from 'lib/ui/layout/Page'
import withStyles from '@material-ui/core/styles/withStyles'
import {spacing} from 'lib/ui/theme'
import Typography from '@material-ui/core/Typography'
import TextField from '@material-ui/core/TextField'
import {fieldError} from 'lib/form'
import FormControl from '@material-ui/core/FormControl'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Checkbox from '@material-ui/core/Checkbox'
import Button from '@material-ui/core/Button'
import {useEventRoutes} from 'organization/Event/EventRoutes'
import {useHistory} from 'react-router-dom'
import {areaRoutes} from 'organization/Event/Area/AreaRoutes'
import {ZoomAreasBreadcrumbs} from 'organization/Event/Page/PageBreadcrumbs'
import {useQueryClient} from 'react-query'
import styled from 'styled-components'
import ConfirmUnsavedChangesDialog from 'organization/Event/Configurable/ConfirmUnsavedChangesDialog'

interface CreateAreaData {
  name: string
  requires_approval: boolean
  allows_multiple_devices: boolean
  zoom_enable_q_and_a: boolean
  zoom_enable_language_interpretation: boolean
  zoom_enable_sign_language_interpretation: boolean
}

export default function CreateAreaForm() {
  const {register, errors, handleSubmit} = useForm({
    mode: 'onBlur',
  })
  const [submitting, setSubmitting] = useState(false)
  const [showCancelDialog, setShowCancelDialog] = useState(false)
  const [serverError, setServerError] = useState<
    ValidationError<CreateAreaData>
  >(null)
  const history = useHistory()
  const createArea = useCreateArea()
  const eventRoutes = useEventRoutes()
  const {event} = useEvent()
  const queryClient = useQueryClient()

  const goToArea = (area: Area) => {
    history.push(areaRoutes({area, eventRoutes}).root)
  }

  const cancel = () => {
    setShowCancelDialog(true)
  }

  const handleCancelConfirm = () => {
    history.push(eventRoutes.areas.root)
  }

  const handleCancelCancel = () => {
    setShowCancelDialog(false)
  }

  const handleSaveAndClose = () => {
    handleSubmit(submit)()
    setShowCancelDialog(false)
  }

  const submit = (data: CreateAreaData) => {
    setSubmitting(true)
    setServerError(null)
    createArea(data)
      .then((newArea: Area) => {
        queryClient.invalidateQueries(['events', event.id, 'areas'])
        goToArea(newArea)
      })
      .catch((e) => {
        setServerError(e)
        setSubmitting(false)
      })
  }

  const nameError = fieldError('name', {form: errors, response: serverError})

  return (
    <ZoomAreasBreadcrumbs page="Create">
      <Page>
        <Title variant="h5" align="left">
          Create Area
        </Title>
        <StyledForm onSubmit={handleSubmit(submit)}>
          <TextField
            label="Area Name"
            name="name"
            fullWidth
            variant="outlined"
            inputProps={{
              ref: register({
                required: 'Name is required',
              }),
              'aria-label': 'area name input',
            }}
            error={Boolean(nameError)}
            helperText={nameError}
          />

          <Title variant="h5" align="left" style={{marginTop: spacing[6]}}>
            Zoom Settings
          </Title>

          <MutedNote>
            The Zoom settings below are only able to be changed when the area is
            created
          </MutedNote>

          <Title variant="h6" align="left">
            Security Settings
          </Title>
          <FieldsHolder>
            <StyledFieldset required fullWidth>
              <FormControlLabel
                inputRef={register}
                name="requires_approval"
                aria-label="toggle requires approval to join meeting"
                control={<Checkbox defaultChecked={true} />}
                label="Requires approval to join area meetings"
              />
              <Info>
                <strong>This is a security feature</strong> to prevent
                non-registered attendees from joining your meetings.
                <br />
                <br />
                Enabling this won't allow users to just copy the Zoom links and
                share them with others.
              </Info>
            </StyledFieldset>
            <StyledFieldset required fullWidth>
              <FormControlLabel
                inputRef={register}
                name="allows_multiple_devices"
                aria-label="toggle allows multiple devices"
                control={<Checkbox defaultChecked={false} />}
                label="Same user can join from multiple devices"
              />
              <Info>
                Enabling this will allow users to join Zoom from multiple
                devices (e.g., laptop and phone) simultaneously.{' '}
                <strong>This is a security risk</strong> as an attendee could
                share their personal link with others, allowing multiple users
                to join this area.
              </Info>
            </StyledFieldset>
          </FieldsHolder>
          <Title variant="h6" align="left">
            Question and Answer Settings
          </Title>
          <FieldsHolder>
            <StyledFieldset required fullWidth>
              <FormControlLabel
                inputRef={register}
                name="zoom_enable_q_and_a"
                aria-label="toggle enable q and a"
                control={<Checkbox defaultChecked={false} />}
                label="Enable Zoom Q&A"
              />
              <Info>
                Allow attendees to ask questions for the host and panelists to
                answer
              </Info>
            </StyledFieldset>
          </FieldsHolder>
          <Title variant="h6" align="left">
            Interpretation Settings
          </Title>
          <FieldsHolder>
            <StyledFieldset required fullWidth>
              <FormControlLabel
                inputRef={register}
                name="zoom_enable_language_interpretation"
                aria-label="toggle enable language interpretation"
                control={<Checkbox defaultChecked={true} />}
                label="Enable language interpretation"
              />
              <Info>
                Allow host to assign participants as interpreters who can
                interpret one language into another in real-time. Host can
                assign interpreters when scheduling or during the meeting.
              </Info>
            </StyledFieldset>
            <StyledFieldset required fullWidth>
              <FormControlLabel
                inputRef={register}
                name="zoom_enable_sign_language_interpretation"
                aria-label="toggle enable sign language interpretation"
                control={<Checkbox defaultChecked={true} />}
                label="Enable sign language interpretation"
              />
              <Info>
                Allow host to assign participants as sign language interpreters
                who can interpret one language into sign language in real-time.
                Host can assign interpreters when scheduling or during the
                meeting.
              </Info>
            </StyledFieldset>
          </FieldsHolder>
          {serverError && serverError.message && (
            <ErrorText>{serverError.message}</ErrorText>
          )}
          <ButtonRow>
            <Button
              variant="contained"
              color="primary"
              disabled={submitting}
              type="submit"
              aria-label="create area"
            >
              Create Area
            </Button>
            <Button
              variant="outlined"
              color="default"
              aria-label="cancel area creation"
              onClick={cancel}
              disabled={submitting}
            >
              Cancel
            </Button>
          </ButtonRow>
        </StyledForm>

        <ConfirmUnsavedChangesDialog
          open={showCancelDialog}
          onDiscard={handleCancelConfirm}
          onCancel={handleCancelCancel}
          onSubmit={handleSaveAndClose}
          expandable={false}
        />
      </Page>
    </ZoomAreasBreadcrumbs>
  )
}

function useCreateArea() {
  const {client} = useOrganization()
  const {event} = useEvent()
  const url = api(`/events/${event.id}/areas`)

  return (data: CreateAreaData) => client.post<Area>(url, data)
}

const Title = withStyles({
  root: {
    marginBottom: spacing[4],
  },
})(Typography)

const StyledForm = styled.form`
  padding-bottom: 20px;

  & > div {
    width: 100%;
  }
`

const FieldsHolder = styled.div`
  margin-bottom: ${(props) => `${props.theme.spacing[4]}`};
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.2rem;

  @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }

  & > div {
    width: 100%;
  }
`

const Info = styled.div`
  font-size: 0.9rem;
  opacity: 0.7;
  margin-top: 4px;
  margin-left: 32px;
  font-style: italic;
`

const ErrorText = styled.div`
  color: #1a1a1a;
  background-color: #ffebee;
  border: 1px solid #ffcdd2;
  border-left: 4px solid #d32f2f;
  font-size: 0.875rem;
  margin: 16px 0;
  text-align: left;
  padding: 16px;
  border-radius: 4px;
  font-weight: 500;
  box-shadow: 0 2px 4px rgba(211, 47, 47, 0.1);
`

const ButtonRow = styled.div`
  margin-top: 24px;
  display: flex;
  justify-content: left;
  gap: 15px;

  @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
    justify-content: center;
  }
`

const StyledFieldset = styled(FormControl)`
  width: 100%;
  border-radius: 8px;
  padding: 0 20px;
  cursor: pointer;
  padding-bottom: 10px;

  justify-content: top;

  &:hover {
    background: #f5f5f5;
  }
`

const MutedNote = styled.div`
  display: block;
  text-align: left;
  opacity: 0.7;
  font-size: 0.9rem;
  font-style: italic;
  margin-bottom: 32px;
`
